import type { PoiFact, PoiVerification } from '../../types/api';
import { Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { EntityRef } from '../EntityRef';
import { DetailSection } from './DetailSection';
import { ComputedFactsSection } from './facts/ComputedFactsSection';

function formatWhen(value?: string | null) {
    return value ? new Date(value).toLocaleString() : null;
}

export function PoiFactsSection({ facts }: { facts: PoiFact[] }) {
    return <ComputedFactsSection entityKind="poi" facts={facts} />;
}

export function PoiVerificationsSection({
    verifications,
}: {
    verifications: PoiVerification[];
}) {
    const { t } = useTranslation();
    const empty = t('common.empty');

    return (
        <DetailSection
            title={t('detail.poiVerifications', { count: verifications.length })}
            description={t('detail.poiVerificationsHint')}
        >
            {verifications.length === 0 ? (
                <Text size="sm" c="dimmed">
                    {t('detail.noPoiVerifications')}
                </Text>
            ) : (
                <Table withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>{t('fields.verificationType')}</Table.Th>
                            <Table.Th>{t('fields.result')}</Table.Th>
                            <Table.Th>{t('fields.method')}</Table.Th>
                            <Table.Th>{t('fields.visit')}</Table.Th>
                            <Table.Th>{t('fields.case')}</Table.Th>
                            <Table.Th>{t('fields.verifiedAt')}</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {verifications.map((verification) => (
                            <Table.Tr key={verification.id}>
                                <Table.Td>{verification.verificationType ?? empty}</Table.Td>
                                <Table.Td>{verification.verificationResult ?? empty}</Table.Td>
                                <Table.Td>{verification.verificationMethod ?? empty}</Table.Td>
                                <Table.Td>
                                    {verification.visitId ? (
                                        <EntityRef
                                            title={t('entities.visit')}
                                            id={verification.visitId}
                                            href={`/visits/${verification.visitId}`}
                                            hideId
                                        />
                                    ) : (
                                        empty
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    {verification.caseId ? (
                                        <EntityRef
                                            title={t('entities.case')}
                                            id={verification.caseId}
                                            href={`/cases/${verification.caseId}`}
                                            hideId
                                        />
                                    ) : (
                                        empty
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    {formatWhen(verification.verifiedAt) ?? empty}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            )}
        </DetailSection>
    );
}
